import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';

interface WaveSurferVisualizerProps {
  isRecording: boolean;
  onRecordingStart: () => void;
  onRecordingStop: (audioBlob: Blob) => void;
  onRecordingCancel: () => void;
  className?: string;
}

export interface WaveSurferVisualizerRef {
  cancelRecording: () => void;
}

export const WaveSurferVisualizer = forwardRef<WaveSurferVisualizerRef, WaveSurferVisualizerProps>(
  ({ isRecording, onRecordingStart, onRecordingStop, onRecordingCancel, className = '' }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const recordPluginRef = useRef<RecordPlugin | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [isActuallyRecording, setIsActuallyRecording] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isCancelledRef = useRef<boolean>(false);

    // Initialize WaveSurfer
    useEffect(() => {
      if (!containerRef.current) return;

      const initWaveSurfer = async () => {
        try {
          console.log('Initializing WaveSurfer...');

          // Create WaveSurfer instance
          const wavesurfer = WaveSurfer.create({
            container: containerRef.current!,
            waveColor: '#8B5CF6',
            progressColor: '#A855F7',
            cursorColor: '#FFFFFF',
            barWidth: 2,
            barGap: 1,
            barRadius: 2,
            height: 60,
            normalize: true,
            fillParent: true,
          });

          // Create Record plugin
          const recordPlugin = wavesurfer.registerPlugin(
            RecordPlugin.create({
              scrollingWaveform: true,
              continuousWaveform: false,
              renderRecordedAudio: false,
              scrollingWaveformWindow: 10, // Slower scrolling - 10 seconds to cross the screen
            })
          );

          wavesurferRef.current = wavesurfer;
          recordPluginRef.current = recordPlugin;

          // Set ready immediately after WaveSurfer creation
          setIsReady(true);

          // Handle Record plugin events
          recordPlugin.on('record-end', (blob: Blob) => {
            console.log('Recording ended', blob, 'isCancelled:', isCancelledRef.current);
            setIsActuallyRecording(false);

            // Only send for transcription if not cancelled
            if (!isCancelledRef.current) {
              onRecordingStop(blob);
            } else {
              // Just call cancel handler
              onRecordingCancel();
            }
          });

          // Handle recording progress
          recordPlugin.on('record-progress', (time: number) => {
            console.log('Recording progress:', time);
          });

          // Handle errors
          wavesurfer.on('error', (err) => {
            console.error('WaveSurfer error:', err);
            setError('Failed to initialize audio');
          });

          // Handle record plugin errors through try-catch in recording functions
        } catch (error) {
          console.error('Error initializing WaveSurfer:', error);
          setError('Failed to initialize audio');
        }
      };

      initWaveSurfer();

      // Cleanup
      return () => {
        if (wavesurferRef.current) {
          wavesurferRef.current.destroy();
          wavesurferRef.current = null;
          recordPluginRef.current = null;
        }
      };
    }, [onRecordingStart, onRecordingStop]);

    // Expose cancel function to parent component
    useImperativeHandle(ref, () => ({
      cancelRecording: () => {
        console.log('Recording cancelled via ref');
        isCancelledRef.current = true;
        if (recordPluginRef.current?.isRecording()) {
          recordPluginRef.current.stopRecording();
        }
      },
    }));

    // Handle recording start/stop
    useEffect(() => {
      if (!recordPluginRef.current || !isReady) {
        console.log('Not ready for recording:', {
          recordPlugin: !!recordPluginRef.current,
          isReady,
        });
        return;
      }

      const handleRecording = async () => {
        try {
          if (isRecording && !recordPluginRef.current!.isRecording()) {
            console.log('Starting recording...');
            await recordPluginRef.current!.startRecording();
            setIsActuallyRecording(true);
            onRecordingStart();
          } else if (!isRecording && recordPluginRef.current!.isRecording()) {
            console.log('Stopping recording...');
            await recordPluginRef.current!.stopRecording();
            // Note: isActuallyRecording will be set to false in the record-end event handler
          }
        } catch (error) {
          console.error('Error with recording:', error);
          onRecordingCancel();
        }
      };

      handleRecording();
    }, [isRecording, isReady, isActuallyRecording, onRecordingStart, onRecordingCancel]);

    // Recording timer
    useEffect(() => {
      let interval: NodeJS.Timeout;

      if (isActuallyRecording) {
        setRecordingTime(0);
        interval = setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);
      } else {
        setRecordingTime(0);
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }, [isActuallyRecording]);

    const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (!isRecording) {
      return (
        <div className={`flex items-center justify-center h-15 ${className}`}>
          <div className="text-kind-text-secondary text-sm">
            Click the microphone to start recording
          </div>
        </div>
      );
    }

    return (
      <div className={`w-full relative ${className}`} style={{ minHeight: '60px' }}>
        <div
          ref={containerRef}
          className="w-full h-15 rounded-lg pointer-events-none"
          style={{
            minHeight: '60px',
            zIndex: 1,
          }}
        />

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-500/20 rounded-lg pointer-events-none z-10">
            <div className="text-red-400 text-sm text-center">{error}</div>
          </div>
        )}

        {/* Recording timer overlay */}
        {isActuallyRecording && (
          <div className="absolute top-2 left-2 bg-kind-graphite/80 rounded px-2 py-1 pointer-events-none z-10">
            <span className="text-xs text-kind-text-primary font-mono">
              {formatTime(recordingTime)}
            </span>
          </div>
        )}

        {/* Loading state */}
        {!isReady && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-kind-graphite/50 rounded-lg pointer-events-none z-10">
            <div className="flex flex-col items-center space-y-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-kind-purple rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-kind-purple rounded-full animate-bounce"
                  style={{ animationDelay: '0.1s' }}
                ></div>
                <div
                  className="w-2 h-2 bg-kind-purple rounded-full animate-bounce"
                  style={{ animationDelay: '0.2s' }}
                ></div>
              </div>
              <div className="text-xs text-kind-text-secondary">Initializing audio...</div>
            </div>
          </div>
        )}
      </div>
    );
  }
);
